interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: string;
  priority: string;
}

const BASE_URL = 'https://mysalarycalculator.net';
const TODAY = new Date().toISOString().split('T')[0];

export const generateSitemapUrls = (
  masterData: Record<string, unknown>,
  blogArticles: Array<{ slug: string; date: string; category: string }>
): SitemapUrl[] => {
  const urls: SitemapUrl[] = [];

  // Homepage
  urls.push({
    loc: `${BASE_URL}/`,
    lastmod: TODAY,
    changefreq: 'weekly',
    priority: '1.0',
  });

  // Blog listing page
  urls.push({
    loc: `${BASE_URL}/blog`,
    lastmod: TODAY,
    changefreq: 'daily',
    priority: '0.9',
  });

  // States directory page
  urls.push({
    loc: `${BASE_URL}/states`,
    lastmod: TODAY,
    changefreq: 'weekly',
    priority: '0.9',
  });

  // Sitemap page
  urls.push({
    loc: `${BASE_URL}/sitemap`,
    lastmod: TODAY,
    changefreq: 'weekly',
    priority: '0.4',
  });

  const countries = (masterData as { countries: Record<string, unknown> }).countries as Record<string, Record<string, unknown>>;

  // Calculator location pages
  Object.entries(countries).forEach(([countrySlug, country]) => {
    // Country level
    urls.push({
      loc: `${BASE_URL}/${countrySlug}`,
      lastmod: TODAY,
      changefreq: 'monthly',
      priority: '0.9',
    });

    if (countrySlug === 'uk') {
      const regions = (country as { regions: Record<string, Record<string, unknown>> }).regions;
      if (regions) {
        Object.entries(regions).forEach(([regionSlug]) => {
          urls.push({
            loc: `${BASE_URL}/uk/${regionSlug}`,
            lastmod: TODAY,
            changefreq: 'weekly',
            priority: '0.7',
          });
        });
      }
    } else {
      const states = (country as { states: Record<string, Record<string, unknown>> }).states;
      if (states) {
        Object.entries(states).forEach(([stateSlug, state]) => {
          // State detail page (/states/ny)
          urls.push({
            loc: `${BASE_URL}/states/${stateSlug}`,
            lastmod: TODAY,
            changefreq: 'monthly',
            priority: '0.7',
          });

          // State calculator hub (/usa/ny)
          urls.push({
            loc: `${BASE_URL}/${countrySlug}/${stateSlug}`,
            lastmod: TODAY,
            changefreq: 'monthly',
            priority: '0.8',
          });

          const cities = (state as { cities: Record<string, unknown> }).cities as Record<string, unknown> | undefined;
          if (cities) {
            Object.keys(cities).forEach((citySlug) => {
              urls.push({
                loc: `${BASE_URL}/${countrySlug}/${stateSlug}/${citySlug}`,
                lastmod: TODAY,
                changefreq: 'weekly',
                priority: '0.7',
              });
            });
          }
        });
      }
    }
  });

  // Blog article pages
  blogArticles.forEach((article) => {
    urls.push({
      loc: `${BASE_URL}/blog/${article.slug}`,
      lastmod: article.date ? parseDateToISO(article.date) : TODAY,
      changefreq: 'monthly',
      priority: '0.6',
    });
  });

  return urls;
};

const parseDateToISO = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
    return date.toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

export const generateSitemapXML = (urls: SitemapUrl[]): string => {
  const urlEntries = urls
    .map(
      (u) => `  <url>
    <loc>${u.loc}</loc>
    <lastmod>${u.lastmod}</lastmod>
    <changefreq>${u.changefreq}</changefreq>
    <priority>${u.priority}</priority>
  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;
};

export const getSitemapStats = (
  masterData: Record<string, unknown>,
  blogArticles: Array<{ slug: string; date: string; category: string }>
) => {
  const urls = generateSitemapUrls(masterData, blogArticles);
  const calculatorPages = urls.filter((u) => !u.loc.includes('/blog'));
  const blogPages = urls.filter((u) => u.loc.includes('/blog'));

  return {
    total: urls.length,
    calculatorPages: calculatorPages.length,
    blogPages: blogPages.length,
    urls,
  };
};
